<script setup>
import {ref, onMounted, computed, watch, onUnmounted, reactive, nextTick} from 'vue';
import axios from 'axios';
import { Link } from '@inertiajs/vue3';
import Modal from "../../components/Modal.vue";
import {debounce} from "lodash";
import Tooltip from "../../components/Tooltip.vue";

const props = defineProps({
    event: {
        type: Object,
    },
    activity: {
        type: Object,
    },
    organization: {
        type: Object,
    },
    round: {
        type: String,
    },
    carryovers: {
        type: Object
    }
});

const data = ref([]);
const loading = ref(true);
const pagination = ref({});
const riderFocusedIndex = ref(null);
const horseFocusedIndex = ref(null);
const bullFocusedIndex = ref(null);
const drawSlots = ref(100);
const drawPageSize = ref(100);
const currentPage = ref(1);
const numPages = ref(1);
const publishDrawDialog = ref(false);
const searchModeOn = ref(false);
const searchText = ref(null);
const tooltipPosition = ref({ top: 0, left: -999 });
const tooltipItems = ref([]);
const rolloverClickIndex = ref(null);
const highlightedIndex = ref(-1);
const horsesLoaded = ref(false);
const ridesLoaded = ref(false);
const horsesList = ref([]);
const ridersList = ref([]);
const scoresInEdit = ref([]);
const searchForRowIndex = ref(-1);
const searchForRiderResults = ref([]);
const searchForHorseResults = ref([]);
const searchForBullResults = ref([]);
const scoreSaveWarnings = reactive({});
const scoreErrors = reactive({});
const horseErrors = reactive({});
const currentPageBeforeSearch = ref(1);
const pendingRequests = ref(new Map()); // Track pending incentives
const searchComplete = ref(false)

watch(() => searchModeOn.value, function() {
    if (searchModeOn.value) {
        numPages.value = 1
    } else {
        numPages.value = Math.ceil(drawSlots.value / drawPageSize.value)
    }
});

const slots = computed(() => {
    //searching just use what was returned
    if (searchModeOn.value) {
        return data.value
    }

    //check if we need to adjust last page not to fill all slots
    if (currentPage.value == numPages.value) {
        const remainingSlots = Number(drawSlots.value) - ((Number(numPages.value)-1) * Number(drawPageSize.value))
        if (remainingSlots < Number(drawPageSize.value)) {
            return data.value.slice(0, remainingSlots)
        }
    }

    return data.value
})

const setRiderFocus = (index, isFocused) => {
    riderFocusedIndex.value = isFocused ? index : null;
};

const setHorseFocus = (index, isFocused) => {
    searchForHorseResults.value = []
    searchForRowIndex.value = -1
    horseFocusedIndex.value = isFocused ? index : null;
};

const setBullFocus = (index, isFocused) => {
    bullFocusedIndex.value = isFocused ? index : null;
};
const fetchData = async (page) => {
    let url = null

    if (searchModeOn.value) {
        url = '/ajax/draw/' + props.activity.id + '/round/' + props.round + '?q=' + searchText.value.trim()
    } else {
        url = '/ajax/draw/' + props.activity.id + '/round/' + props.round + '?page=' + page
    }

    loading.value = true;
    try {
        const response = await axios.get(url);
        data.value = response.data
    } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error fetching data:');
    } finally {
        loading.value = false;
    }
};

const resizeDraw = () => {
    if (canResizeDraw(drawSlots.value)) {
        axios.patch('/ajax/activity/' + props.activity.id, {
            'total_slots': drawSlots.value,
        })
            .then(result => {
            })
            .catch(error => {
                console.error('An error resizing draw occurred', error);
                alert('An error resizing draw occurred');
            })
            .finally(() => {
            });
    } else {
        alert('Cannot resize to ' + drawSlots.value + ' because filled slots exist above the new size requested.')
        drawSlots.value = props.activity.total_slots
    }
}

const addSelectedRider = (index, id, name) => {
    data.value[index].user_id = id
    data.value[index].user_name = name

    addToDraw(index)
}

const addSelectedHorse = (index, id, name) => {
    data.value[index].horse_id = id
    data.value[index].horse_name = name

    updateHorseInDraw(index)
}

const addSelectedBull = (index, id, name) => {
    data.value[index].bull_id = id
    data.value[index].bull_name = name

    updateBullInDraw(index)
}

const removeUser = (index) => {
    if (data.value[index].horse_id) {
        axios.patch('/ajax/user-activity/' + data.value[index].id, {
            'user_id': null,
            'user_name': null,
        })
            .then(result => {
            })
            .catch(error => {
                console.error('An error occurred removing a rider', error);
                alert('An error occurred removing a rider');
            })
            .finally(() => {
            });
    } else {
        axios.delete('/ajax/user-activity/' + data.value[index].id)
            .then(result => {
            })
            .catch(error => {
                console.error('An error occurred removing user', error);
                alert('An error occurred removing user');
            })
            .finally(() => {
            });
    }
}

const removeHorse = (index) => {
    if (data.value[index].user_id) {
        axios.patch('/ajax/user-activity/' + data.value[index].id, {
            'horse_id': null,
            'horseName': null,
        })
            .then(result => {
            })
            .catch(error => {
                console.error('An error occurred removing horse', error);
                alert('An error occurred removing horse');
            })
            .finally(() => {
            });
    } else {
        axios.delete('/ajax/user-activity/' + data.value[index].id)
            .then(result => {
            })
            .catch(error => {
                console.error('An error occurred removing user', error);
                alert('An error occurred removing user');
            })
            .finally(() => {
            });
    }
}

const removeBull = (index) => {
    axios.patch('/ajax/user-activity/' + data.value[index].id, {
        'bull_id': null,
        'bullName': null,
    })
        .then(result => {
        })
        .catch(error => {
            console.error('An error occurred removing bull', error);
            alert('An error occurred removing bull');
        })
        .finally(() => {
        });
}

const updateHorseInDraw = (index) => {
    if (!data.value[index].horse_id && data.value[index].horse_name) {
        if (!confirm("You're about to add a new horse to the database. Make sure you check the search field first. Are you sure you want to add " + data.value[index].horse_name + " to the database?")) {
            return;
        }
    }

    axios.patch('/ajax/user-activity/' + data.value[index].id, {
        'horse_id': data.value[index].horse_id ?? null,
        'horseName': data.value[index].horse_name,
    })
        .then(result => {
            if (!horsesList.value.some(horse => horse.id === result.data.horse.id)) {
                horsesList.value.push({
                    id: result.data.horse.id,
                    name: result.data.horse.name,
                    rrrs_id: result.data.horse.rrrs_id,
                });
            }
        })
        .catch(error => {
            if (error.response.data == "Horse already exists") {
                horseErrors[index] = "Horse already exists";
            } else {
                console.error('An error occurred updating horse', error);
                alert('An error occurred updating horse');
            }
        })
        .finally(() => {
            setHorseFocus(index, false)
        });

}

const updateBullInDraw = (index) => {
    axios.patch('/ajax/user-activity/' + data.value[index].id, {
        'bull_id': data.value[index].bull_id ?? null,
        'bullName': data.value[index].bull_name,
    })
        .then(result => {
        })
        .catch(error => {
            console.error('An error occurred updating bull', error);
            alert('An error occurred updating bull');
        })
        .finally(() => {
            setBullFocus(index, false)
        });

}

const deleteDrawEntry = (index) => {
    const id = data.value[index].id
    const draw = data.value[index].draw

    clearSlot(index)
    data.value[index].id = null
    data.value[index].draw = draw

    axios.delete('/ajax/user-activity/' + id)
        .then(result => {
        })
        .catch(error => {
            console.error('An error occurred deleting draw entry', error);
            alert('An error occurred deleting draw entry');
        })
        .finally(() => {
        });
}

const addToDraw = (index) => {
    let url = null
    if (props.round) {
        url = '/ajax/draw/' + props.activity.id + '/round/' + props.round
    } else {
        url = '/ajax/draw/' + props.activity.id
    }

    if (data.value[index].id && (data.value[index].user_id || data.value[index].horse_id)) {
        const payload = data.value[index].user_id
            ? { user_id: data.value[index].user_id }
            : { user_name: data.value[index].user_name };

        axios.patch('/ajax/user-activity/' + data.value[index].id, payload)
            .then(result => {
                data.value[index].id = result.data.id
                data.value[index].user_id = result.data.user_id
                data.value[index].activity_scored = true
            })
            .catch(error => {
                console.error('An error occurred updating draw', error);
                alert('An error occurred updating user in draw');
            })
            .finally(() => {
                setRiderFocus(index, false)
            });
    } else {
        axios.post(url, {
            'user_id': data.value[index].user_id,
            'user_name': data.value[index].user_name,
            'slotNumber': data.value[index].draw,
        })
            .then(result => {
                data.value[index].id = result.data.id
                data.value[index].user_id = result.data.user_id
                data.value[index].activity_scored = true
            })
            .catch(error => {
                console.error('An error occurred adding to draw', error);
                alert('An error occurred adding to draw');
            })
            .finally(() => {
                setRiderFocus(index, false)
            });
    }
}

const resetSearch = (event) => {
    searchModeOn.value = false
    searchText.value = null
    event.target.blur();
    fetchData(currentPageBeforeSearch.value)
}

const search = debounce(async () => {
    if (searchText.value.trim() === '') {
        searchModeOn.value = false
        return;
    }

    searchModeOn.value = true
    currentPageBeforeSearch.value = currentPage.value
    fetchData(1)
}, 300);

const searchRider = debounce(async (index) => {
    if (data.value[index].user_name.trim() === '') {
        searchForRowIndex.value = -1
        searchForRiderResults.value = []
        return;
    }

    if (ridesLoaded.value) {
        const query = data.value[index].user_name.toLowerCase().trim();
        searchForRiderResults.value = ridersList.value.filter(rider =>
            rider.name.toLowerCase().includes(query)
        );
    } else {
        try {
            const response = await axios.get('/ajax/rider/search', {params: {query: data.value[index].user_name.trim()}});
            searchForRiderResults.value = response.data.data;
        } catch (error) {
            console.error('Error fetching search rider results:', error);
            alert('Error fetching search rider results:');
        }
    }
}, 300);

const searchHorse = debounce(async (index) => {
    if (data.value[index].horse_name.trim() === '') {
        searchForHorseResults.value = []
        return;
    }

    if (horsesLoaded.value) {
        const query = data.value[index].horse_name.toLowerCase().trim();
        searchForHorseResults.value = horsesList.value.filter(horse =>
            horse.name.toLowerCase().startsWith(query)
        );
        searchComplete.value = true;
    } else {
        try {
            const response = await axios.get('/ajax/horse/search', {params: {query: data.value[index].horse_name.trim()}});
            searchForHorseResults.value = response.data.data;
            searchComplete.value = true;
        } catch (error) {
            console.error('Error fetching search horse results:', error);
            alert('Error fetching search horse results:');
        }
    }

}, 300);

const searchBull = debounce(async (index) => {
    if (data.value[index].bull_name.trim() === '') return;

    try {
        const response = await axios.get('/ajax/bull/search', { params: { query: data.value[index].user_name.trim() } });
        searchForBullResults.value = response.data.data;
    } catch (error) {
        console.error('Error fetching search bull results:', error);
        alert('Error fetching search bull results:');
    }
}, 300);

const randomizeDraw = () => {
    if (confirm('You are about to randomize your draw. This cannot be reversed. Are you sure you want to randomize?')) {
        axios.post('/ajax/randomize-draw/' + props.activity.id)
            .then(result => {
            })
            .catch(error => {
                console.error('An error occurred randomizing draw', error);
                alert('An error occurred randomizing draw');
            })
            .finally(() => {
            });
    }
}

const publishDraw = () => {
    axios.post('/ajax/publish-draw/' + props.activity.id)
        .then(result => {
            props.activity.publish_draw = true
            closeConfirmModal()
        })
        .catch(error => {
            console.error('An error occurred publishing draw', error);
            alert('An error occurred publishing draw');
        })
        .finally(() => {
        });
}

const unpublishDraw = () => {
    axios.post('/ajax/unpublish-draw/' + props.activity.id)
        .then(result => {
            props.activity.publish_draw = false
            closeConfirmModal()
        })
        .catch(error => {
            console.error('An error occurred unpublishing draw', error);
            alert('An error occurred unpublishing draw');
        })
        .finally(() => {
        });
}

const closeConfirmModal = () => {
    publishDrawDialog.value = false
}

const penaltyDisabled = (index) => {
    if ((!data.value[index].score && data.value[index].user_id) || data.value[index].score_entered) {
        return false
    }

    return true
}

const scoreFocused = (index) => {
    //turn warning on for other score not yet saved
    scoresInEdit.value.forEach((item) => {
        scoreSaveWarnings[item] = true
    });
    scoresInEdit.value.push(index)

    scoreSaveWarnings[index] = false;
}

const editScore = (index) => {
    //turn warning on for other score not yet saved
    scoresInEdit.value.forEach((item) => {
        scoreSaveWarnings[item] = true
    });

    scoresInEdit.value.push(index)
    data.value[index].score_entered = data.value[index].score
    scoreSaveWarnings[index] = false;
}

const isTimeScore = (score) => {
    return /^[0-9.,]+$/.test(score);
}

const recordScore = (index) => {
    //replace commas with periods
    if (typeof data.value[index].score_entered === "string") {
        if (isTimeScore(data.value[index].score_entered)) {
            data.value[index].score_entered = data.value[index].score_entered.replace(/,/g, '.');
            if (isNaN(Number(data.value[index].score_entered))) {
                scoreErrors[index] = true
                return;
            } else {
                scoreErrors[index] = false
            }
        }
    }
    const scoreToPost =  data.value[index].score_entered

    scoresInEdit.value = scoresInEdit.value.filter((item) => item !== index);
    scoreSaveWarnings[index] = false
    data.value[index].score = scoreToPost
    data.value[index].score_entered = null

    axios.patch('/ajax/score/' + data.value[index].score_id, {
        score: scoreToPost
    })
        .then(result => {
        })
        .catch(error => {
            console.error('An error occurred updating score', error);
            alert('An error occurred updating score');
        })
        .finally(() => {
        });

    const nextEditableScoreField = findNextEditableScore(index)
    if (nextEditableScoreField) {
        nextEditableScoreField.focus();
    }
}

const findNextEditableScore = (index) => {
    let nextField = null

    for (let i = index; i < slots.value.length; i++) {
        if (!slots.value[i].score || scoresInEdit.value.includes(i)) {
            nextField = document.querySelector('#score-'+i)
            if (nextField) {
                return nextField
            } else {
                return null
            }
        }
    }

    return null
}

const setPenalty = (index) => {
    switch (data.value[index].penalty) {
        case 'clear':
            data.value[index].score_entered = ''
            recordScore(index)
            break;
        case 'NT':
        case 'DQ':
        case 'Broke':
            data.value[index].score_entered = data.value[index].penalty
            recordScore(index)
            break;
        case '5':
        case '10':
        case '15':
            data.value[index].score_entered = data.value[index].score_entered.replace(/,/g, '.');
            if (isNaN(Number(data.value[index].score_entered))) {
                scoreErrors[index] = true
                data.value[index].penalty = null
                return;
            } else {
                scoreErrors[index] = false
                data.value[index].score_entered = Number(data.value[index].score_entered) + Number(data.value[index].penalty)
                recordScore(index)
            }
            break;
        default:
            break;
    }

    data.value[index].penalty = null
}

const formatScore = (score) => {
    if (isNaN(score))
        return score

    return Number(score).toFixed(3);
}

const canResizeDraw = (newSize) => {
    let lastUsedSlot = 0

    if (! newSize) {
        return false;
    }

    // slot numbers are 1 based; not 0 based
    if (Number(newSize) < Number(props.activity.last_used_slot)) {
        return false;
    }

    return true;
}

const goToPage = (page, event) => {
    currentPage.value = page
    fetchData(page)
    event.target.blur();
}

const showTooltip = async(rowIndex, event) => {
    await nextTick();
    rolloverClickIndex.value = rowIndex
    const button = event.target;
    const buttonRect = button.getBoundingClientRect();

    const tooltipElement = document.querySelector('.tooltip');
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const tooltipHeight = tooltipRect.height;
    const tooltipWidth = tooltipRect.width;

    tooltipPosition.value = {
        top: buttonRect.top + window.scrollY + buttonRect.height / 2 - tooltipHeight / 2, // Align vertically
        left: buttonRect.left + window.scrollX - tooltipWidth - 10, // Align horizontally
    };

}

const hideTooltip = () => {
    tooltipPosition.value.left = -999
}

const isIncentiveChecked = (index, incentiveId) => {
    if (!data.value.length) {
        return false;
    }

    if (incentiveId == props.activity.id) {
        return data.value[index]?.activity_scored
    }

    return data.value[index]?.scored_incentives.some(item => item.id === incentiveId);
    //return data.value[index]?.scored_incentives.includes(incentiveId) ?? false;
}

const toggleIncentive = async (indexInput, incentive, event) => {
    let index = indexInput;

    // Check if a request is already pending for this incentive
    if (pendingRequests.value.get(`${index}-${incentive.id}`)) return;

    // Set the incentive as pending
    pendingRequests.value.set(`${index}-${incentive.id}`, true);

    if (event.target.checked) {
        if (!isIncentiveChecked(index, incentive.id)) {
            data.value[index].scored_incentives.push({ id: incentive.id});
            await addIncentive(data.value[index].id, incentive.id);
        }
    } else {
        data.value[index].scored_incentives = data.value[index].scored_incentives.filter(i => i.id !== incentive.id);
        await removeIncentive(data.value[index].id, incentive.id);
    }

    // Remove pending state after request finishes
    pendingRequests.value.delete(`${index}-${incentive.id}`);
}

const addIncentive = (userActivityId, incentiveId) => {
    return axios.post(`/ajax/add-incentive/${userActivityId}/${incentiveId}`)
        .catch(error => {
            console.error('An error occurred adding incentive', error);
            alert('An error occurred adding incentive');
        });
}

const removeIncentive = (userActivityId, incentiveId) => {
    return axios.post(`/ajax/remove-incentive/${userActivityId}/${incentiveId}`)
        .catch(error => {
            console.error('An error occurred removing incentive', error);
            alert('An error occurred removing incentive');
        });
};

const moveSlot = async (index) => {
    const oldValue = data.value[index].position
    const newValue = data.value[index].draw

    if (! newValue || newValue < 1) {
        return;
    }

    if (newValue > drawSlots.value) {
        alert('New slot number is out of bounds.');
        data.value[index].draw = oldValue
        return;
    }

    let destinationSlotIndex = findDestinationIndex(newValue)
    let destinationOnDifferentPage = false;
    let destUserActivityId = null;

    if (destinationSlotIndex < 0) {
        destUserActivityId = await getDestinationUserActivityId(newValue)
        destinationOnDifferentPage = destUserActivityId && destUserActivityId >= 0;
    } else {
        destUserActivityId = data.value[destinationSlotIndex].id
    }

    if (destinationOnDifferentPage || (destinationSlotIndex >= 0 && data.value[destinationSlotIndex].id)) {
        const confirmSwitch = confirm('The slot at ' + data.value[index].draw + ' is already populated. Would you like to switch this entry with the entry located at slot ' + data.value[index].draw + '?');
        if (!confirmSwitch) {
            data.value[index].draw = oldValue
            return;
        } else {
            const sourceUserActivityId = data.value[index].id
            axios.patch('/ajax/user-activity-swap', {
                'sourceUserActivityId': sourceUserActivityId,
                'destUserActivityId': destUserActivityId,
            })
                .then(result => {
                })
                .catch(error => {
                    console.error('An error occurred moving slots', error);
                    alert('An error occurred moving slots');
                })
                .finally(() => {
                });
        }
    } else {
        const id = data.value[index].id
        clearSlot(index)
        axios.patch('/ajax/user-activity-clear/' + data.value[index].id)
        data.value[index].id = null

        axios.patch('/ajax/user-activity/' + id, {
                'slotNumber': newValue,
            })
                .then(result => {
                    data.value[index].id = null
                    data.value[index].draw = oldValue
                })
                .catch(error => {
                    console.error('An error occurred moving slots', error);
                    alert('An error occurred moving slots');
                })
                .finally(() => {
        });

    }
}

const getDestinationUserActivityId = async (destinationSlot) => {
    return axios.get('/ajax/draw/' + props.activity.id +  '/check-destination/' + destinationSlot)
        .then(result => {
            return result.data
        })
        .catch(error => {
            console.error('An error occurred checking slots', error);
            alert('An error occurred swapping slots');
            return 0
        })
        .finally(() => {
        });

}

const findDestinationIndex = (slotNumber) => {
    return data.value.findIndex(obj => obj.position === slotNumber);
}

const clearSlot = (index) => {
    //clear all fields except id
    data.value[index].draw = null
    data.value[index].page_draw = null
    data.value[index].position = null
    data.value[index].slcak_draw = null

    data.value[index].activity_scored = false
    data.value[index].bull = null
    data.value[index].bull_id = null
    data.value[index].bull_name = null
    data.value[index].horse = null
    data.value[index].horse_id = null
    data.value[index].horse_name = null
    data.value[index].rider = null
    data.value[index].rider_id = null
    data.value[index].rider_name = null
    data.value[index].score = null
    data.value[index].score_entered = null
    data.value[index].score_id = null
    data.value[index].scored_incentives = []
    data.value[index].team_id = null
    data.value[index].user_fake = null
    data.value[index].user = null
    data.value[index].user_id = null
    data.value[index].user_name = null
}

const handleRiderSearchKeydown = (rowIndex, draw) => {
    searchForRowIndex.value = rowIndex

    const results = searchForRiderResults.value;
    if (!results?.length) return;

    switch (event.key) {
        case 'ArrowDown':
            highlightedIndex.value =
                (highlightedIndex.value + 1) % results.length;
            break;
        case 'ArrowUp':
            highlightedIndex.value =
                (highlightedIndex.value - 1 + results.length) % results.length;
            break;
        case 'Enter':
            if (highlightedIndex.value >= 0) {
                const selected = results[highlightedIndex.value];
                addSelectedRider(rowIndex, selected.id, selected.name);
            }
            break;
        case 'Escape':
            highlightedIndex.value = -1; // Clear highlight on Escape
            searchForRowIndex.value = -1
            searchForRiderResults.value = []
            break;
    }
}

const handleHorseSearchKeydown = (rowIndex, draw, event) => {
    searchComplete.value = false
    horseErrors[rowIndex] = null
    searchForRowIndex.value = rowIndex

    // Only allow letters, numbers, and some control keys
    const allowedKeys = [
        'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', 'Escape', ' ',
        'ArrowUp', 'ArrowDown'
    ];
    const isLetterOrNumber = /^[a-zA-Z0-9]$/.test(event.key);

    if (!isLetterOrNumber && !allowedKeys.includes(event.key)) {
        event.preventDefault(); // Block unwanted character
        return;
    }

    const results = searchForHorseResults.value;
    if (!results?.length) return;

    switch (event.key) {
        case 'ArrowDown':
            highlightedIndex.value =
                (highlightedIndex.value + 1) % results.length;
            break;
        case 'ArrowUp':
            highlightedIndex.value =
                (highlightedIndex.value - 1 + results.length) % results.length;
            break;
        case 'Enter':
            if (highlightedIndex.value >= 0) {
                const selected = results[highlightedIndex.value];
                addSelectedHorse(rowIndex, selected.id, selected.name);
            }
            break;
        case 'Escape':
            highlightedIndex.value = -1; // Clear highlight on Escape
            searchForRowIndex.value = -1
            searchForHorseResults.value = []
            break;
    }
}

const riderIds = new Set(); // Track unique rider IDs

const loadRiders = (url) => {
    axios
        .get(url)
        .then(response => {
            const newRiders = response.data.data.filter(rider => {
                if (!riderIds.has(rider.id)) {
                    riderIds.add(rider.id); // Store the ID to prevent duplicates
                    return true;
                }
                return false;
            });

            ridersList.value = ridersList.value.concat(newRiders);

            // Check for a next page and load it if exists
            if (response.data.links.next) {
                loadRiders(response.data.links.next);
            } else {
                ridesLoaded.value = true;
            }
        })
        .catch(error => {
            console.error('Error loading riders:', error);
        });
};

const horseIds = new Set(); // Track unique horse IDs
const loadHorses = (url) => {
    axios
        .get(url)
        .then(response => {
            const newHorses = response.data.data.filter(horse => {
                if (!horseIds.has(horse.id)) {
                    horseIds.add(horse.id); // Store the ID to prevent duplicates
                    return true;
                }
                return false;
            });

            horsesList.value = horsesList.value.concat(newHorses);

            // Check for a next page and load it if exists
            if (response.data.links.next) {
                loadHorses(response.data.links.next);
            } else {
                horsesLoaded.value = true;
            }
        })
        .catch(error => {
            console.error('Error loading horses:', error);
        });
};

const handleClickOutside = (event) => {
    if (!event.target.closest('.results-area')) {
        searchForRowIndex.value = -1
        searchForRiderResults.value = [];
        searchForHorseResults.value = [];
        searchForBullResults.value = [];
    }
};

onMounted(() => {
    document.title = props.activity.name

    document.addEventListener('click', handleClickOutside);

    drawSlots.value = props.activity.total_slots
    numPages.value = Math.ceil(drawSlots.value / drawPageSize.value)
    fetchData(1);

    loadRiders('/ajax/riders-list');
    loadHorses('/ajax/horses-list');
});

// Remove event listeners when component unmounts
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

window.Echo.channel(`draw.${props.event.id}.${props.activity.id}`)
    .listen('.updated', (event) => {
        data.value = data.value.map((item) => {
            if (item.draw == event.userActivity.draw) {
                if (Number(event.userActivity.draw) > Number(props.activity.last_used_slot)) {
                    props.activity.last_used_slot = event.userActivity.draw
                }
                return  event.userActivity
            }
            return item
        });
    })

    .listen('.swapped', (event) => {
        console.log('event')
        console.log(event.sourceUserActivity)
        console.log(event.destUserActivity)
        const index1 = data.value.findIndex(obj => obj.id === event.sourceUserActivity.id);
        const index2 = data.value.findIndex(obj => obj.id === event.destUserActivity.id);

        if (index1 !== -1 && index2 !== -1) {
            //swap draws
            const oldPosition = data.value[index1].position;
            const newPosition = data.value[index2].position;
            [data.value[index1], data.value[index2]] = [data.value[index2], data.value[index1]];

            data.value[index1].draw = oldPosition
            data.value[index1].position = oldPosition
            data.value[index1].page_draw = oldPosition
            data.value[index1].slack_draw = oldPosition

            data.value[index2].draw = newPosition
            data.value[index2].position = newPosition
            data.value[index2].page_draw = newPosition
            data.value[index2].slack_draw = newPosition

        } else {
            data.value = data.value.map((item) => {
                if (item.draw == event.sourceUserActivity.draw) {
                    return  event.destUserActivity
                }
                if (item.draw == event.destUserActivity.draw) {
                    return  event.sourceUserActivity
                }
                return item
            });
        }
    })

    .listen('.score-updated', (event) => {
        data.value = data.value.map((item) => {
            if (item.score_id == event.score.id) {
                item.score = event.score.score
            }
            return item
        });
    });

window.Echo.channel(`activity.${props.activity.id}`)
    .listen('.updated', (event) => {
        props.activity.publish_draw = event.activity.publish_draw
        props.activity.total_slots = event.activity.total_slots
        drawSlots.value = event.activity.total_slots
        props.activity.last_used_slot = event.activity.last_used_slot
        numPages.value = Math.ceil(drawSlots.value / drawPageSize.value)
    });

window.Echo.channel(`draw-reload.${props.event.id}.${props.activity.id}`)
    .listen('.reload', (event) => {
        window.location.reload()
    });

</script>

<template>
    <div>
        <div class="card">
            <div class="card-header">
                <h2 style="margin-bottom: 0;">
                    <a :href="'/organizer/organizations/'+organization.id">
                        {{ organization.name }}
                    </a>
                    &gt;&gt;
                    <a
                        :href="'/organizer/organizations/' + organization.id + '/events/' + event.id + '/activities'">
                        {{ event.name }}
                    </a>
                    &gt;&gt;
                    <span class='activity_name'>
                        {{ activity.name }}
                    </span>
                </h2>
            </div>
        </div>
        <br>

        <a v-if="activity.publish_draw" class="btn btn-lg btn-link float-left" :href="'/drawsheet/activity/' + props.activity.id">Print Draw</a>
        <div class="text-center">
            <div class="btn-group">
                <button v-if="!activity.publish_draw" @click="randomizeDraw" class="btn btn-primary action-button"> Randomize Draw </button>
                <button v-if="activity.publish_draw" @click="unpublishDraw" class="btn btn-primary action-button"> Unpublish Draw </button>
                <button v-else @click="publishDrawDialog=true" class="btn btn-primary action-button"> Publish Draw </button>
                <a target="_blank" class="btn btn-primary action-button" :href="'/organizer/scoring/' + props.event.id + '/activities/' + props.activity.id + '/payout'"> Calculate Payout </a>
            </div>
            <Modal :visible="publishDrawDialog">
                <template v-slot:header><h3 class="mb-0">Publish Draw</h3></template>
                <template v-slot:body>
                    <p class="mb-0">You're about to post the draw live to the mobile app. Registered users in your draw will be notified. Are you sure you're ready to post the draw?</p>
                </template>
                <template v-slot:footer>
                    <button class="btn btn-outline-dark" @click="closeConfirmModal">Cancel</button>
                    <button class="btn btn-primary" @click="publishDraw">Confirm</button>
                </template>
            </Modal>
        </div>

        <br>

        <div class="form-group row">
            <div class="col-3">
                <label for="search">Search</label>
                <div class="input-group mb-3">
                    <input
                        v-model="searchText"
                        type="text"
                        class="form-control"
                        @input="search()"
                    >
                    <div class="input-group-append">
                        <button @click="resetSearch($event)" title="Search" class="btn btn-primary">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-2">
                <label for="total">Session Limit</label>
                <div class="input-group mb-3">
                    <input v-model="drawSlots" type="number" class="form-control" @blur="resizeDraw" @keyup.enter="resizeDraw">
                    <div class="input-group-append">
                        <button @click="resizeDraw" title="Resize the draw" class="btn btn-primary">
                            <i class="fas fa-arrows-alt-v"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-6">
                <label for="total">Pages</label>
                <div class="d-flex mb-3">
                    <div v-for="(page, pageIndex) in numPages" class="">
                        <button @click="goToPage(pageIndex+1, $event)" class="btn btn-primary" style="margin-right: 10px;"> {{ pageIndex+1 }} </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="bg-white px-3 w-100">
            <!-- Spinner -->
            <div v-if="loading" class="d-flex justify-content-center align-items-center mx-auto">
                <p class="mb-0 mr-2">Loading...</p>
                <svg xmlns="http://www.w3.org/2000/svg" width="3em" height="3em" viewBox="0 0 24 24">
                    <circle cx="4" cy="12" r="1.5" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="1.5;3;1.5"/></circle>
                    <circle cx="12" cy="12" r="3" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="3;1.5;3"/></circle>
                    <circle cx="20" cy="12" r="1.5" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="1.5;3;1.5"/></circle>
                </svg>
            </div>

            <div v-else class="w-100 draw-container">
                <table class="table table-striped results w-100">
                    <thead>
                        <tr style="height: 56px;">
                            <th class="text-left" style="max-width: 70px;">Draw</th>
                            <th class="text-left">Rider</th>
                            <th v-if="!activity.publish_draw" class="text-center">Requests</th>
                            <th v-if="activity.publish_draw" class="text-left">Time</th>
                            <th v-if="activity.publish_draw" class="text-left">Penalty</th>
                            <th class="text-left">Horse</th>
                            <th class="text-left">Carry/Rollovers</th>
                            <th class="text-left">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(draw, rowIndex) in slots" :key="rowIndex">
                            <td style="max-width: 70px;">
                                <div class="input-group">
                                    <input
                                        v-model="draw.draw"
                                        type="number"
                                        class="form-control"
                                        :disabled="!draw.id"
                                        @keyup.enter.stop="moveSlot(rowIndex)"
                                    >
                                </div>
                            </td>
                            <td class="relative">
                                <div v-if="draw.user_id">
                                    <button @click.stop="removeUser(rowIndex)" class="btn btn-danger btn-sm mr-1"><i class="fas fa-times text-center"></i></button>
                                    <span v-if="!draw.user_fake">
                                        <a target="_blank" :href="'/users/view/' + draw.user_id">{{ draw.user_name }}</a>
                                    </span>
                                    <span v-else>{{ draw.user_name }}</span>
                                </div>
                                <div v-else class="input-group">
                                    <input
                                        v-model="draw.user_name"
                                        @input="searchRider(rowIndex)"
                                        autocapitalize="off"
                                        autocorrect="off"
                                        role="combobox"
                                        aria-multiline="false"
                                        type="text"
                                        size="30"
                                        spellcheck="false"
                                        autocomplete="off"
                                        placeholder="Rider Name"
                                        class="form-control"
                                        aria-autocomplete="list"
                                        aria-expanded="false"
                                        @focus="setRiderFocus(rowIndex, true)"
                                        @keydown="handleRiderSearchKeydown(rowIndex, draw)"
                                    >
                                    <div v-if="riderFocusedIndex === rowIndex" class="input-group-append">
                                        <button @click.once="addToDraw(rowIndex)" class="btn btn-primary">
                                            <i class="fas fa-plus"></i>
                                        </button>
                                    </div>
                                    <ul v-if="searchForRiderResults.length > 0 && searchForRowIndex==rowIndex" class="results-area">
                                        <li
                                            v-for="(result, index) in searchForRiderResults"
                                            :key="result.id"
                                            :class="{ 'highlighted': index == highlightedIndex }"
                                            @click="addSelectedRider(rowIndex, result.id, result.name)"
                                        >
                                            {{ result.name }} - {{ result.email }}
                                        </li>
                                    </ul>
                                </div>

                            </td>
                            <td v-if="!activity.publish_draw" class="text-center mx-auto">
                                <div class="tooltip-wrapper">
                                    <img
                                        v-if="draw.request"
                                        src="/images/special-request.png"
                                        class="h-6 w-6 inline"
                                        style="border-radius: 0 !important;"
                                    />
                                    <span class="tooltip-text">{{ draw.request }}</span>
                                </div>
                            </td>
                            <td v-if="activity.publish_draw" class="text-right">
                                <span v-if="draw.score && !scoresInEdit.includes(rowIndex)">
                                    {{ formatScore(draw.score) }}
                                    <button @click="editScore(rowIndex)" class="btn btn-sm btn-primary ml-1">
                                        <i class="fas fa-pencil-alt"></i>
                                    </button>
                                </span>
                                <div class="d-flex align-items-center save-warning" v-else>
                                    <span
                                        v-if="scoreSaveWarnings[rowIndex] && draw.score_entered"
                                        class="mr-2 text-nowrap"
                                    >
                                        Press enter to save
                                    </span>
                                    <span
                                        v-if="scoreErrors[rowIndex] && draw.score_entered"
                                        class="mr-2 text-nowrap"
                                    >
                                        Check time
                                    </span>

                                    <input
                                        @focus="scoreFocused(rowIndex)"
                                        @keyup.enter="recordScore(rowIndex)"
                                        :id="`score-${rowIndex}`"
                                        v-model="draw.score_entered"
                                        size="10"
                                        type="text"
                                        class="form-control text-right flex-shrink-1"
                                        :disabled="!draw.user_id"
                                    >
                                </div>
                            </td>
                            <td v-if="activity.publish_draw">
                                <select
                                    v-model="draw.penalty"
                                    class="form-control"
                                    :disabled="penaltyDisabled(rowIndex)"
                                    @change="setPenalty(rowIndex)"
                                >
                                    <option></option>
                                    <option value="clear">Clear Time</option>
                                    <option value="NT">No Time</option>
                                    <option value="5" :disabled="isNaN(draw.score) || !isTimeScore(draw.score_entered)">+5</option>
                                    <option value="10" :disabled="isNaN(draw.score) || !isTimeScore(draw.score_entered)">+10</option>
                                    <option value="15" :disabled="isNaN(draw.score) || !isTimeScore(draw.score_entered)">+15</option>
                                    <option value="DQ">DQ</option>
                                    <option value="Broke">Broke</option>
                                </select>
                            </td>
                            <td class="relative">
                                <div v-if="draw.horse_id">
                                    <button @click.stop="removeHorse(rowIndex)" class="btn btn-danger btn-sm mr-1"><i class="fas fa-times text-center"></i></button>
                                    {{ draw.horse_name }}
                                </div>
                                <div v-else class="input-group relative">
                                    <input
                                        v-model="draw.horse_name"
                                        @input.stop="searchHorse(rowIndex)"
                                        autocapitalize="off"
                                        autocorrect="off"
                                        role="combobox"
                                        aria-multiline="false"
                                        type="text"
                                        size="43"
                                        spellcheck="false"
                                        autocomplete="off"
                                        placeholder="Horse Name"
                                        class="form-control"
                                        :disabled="!draw.user_id"
                                        aria-autocomplete="list"
                                        aria-expanded="false"
                                        @focus.stop="setHorseFocus(rowIndex, true)"
                                        @keydown="handleHorseSearchKeydown(rowIndex, draw, $event)"
                                    >
                                    <div v-if="horseFocusedIndex === rowIndex" class="input-group-append">
                                        <button @click.stop="updateHorseInDraw(rowIndex)"
                                                class="btn btn-disabled-custom"
                                                :class="{
        'btn-primary': draw.horse_name && draw.horse_name.length && searchComplete && horseFocusedIndex === rowIndex && searchForHorseResults.length == 0,
        'btn-disabled-custom': horseFocusedIndex === rowIndex && (searchForHorseResults.length > 0 || !draw.horse_name)
    }"
                                                :disabled="horseFocusedIndex === rowIndex && (searchForHorseResults.length > 0 || !draw.horse_name)"
                                        >
                                            <i class="fas fa-plus"></i>
                                        </button>
                                    </div>
                                    <ul v-if="searchForHorseResults.length > 0 && searchForRowIndex==rowIndex" class="results-area">
                                        <li
                                            v-for="(result, index) in searchForHorseResults"
                                            :key="result.id"
                                            @click.stop="addSelectedHorse(rowIndex, result.id, result.name)"
                                            :class="{ 'highlighted': index == highlightedIndex }"
                                        >
                                            {{ result.name }}
                                        </li>
                                    </ul>
                                </div>
                                <span
                                    v-if="horseErrors[rowIndex]"
                                    class="mr-2 text-nowrap text-danger text-bold"
                                >
                                        {{ horseErrors[rowIndex] }}
                                    </span>

                            </td>
                            <td>
                                <button v-if="draw.user_id" @click.stop="showTooltip(rowIndex, $event)" type="button" class="btn btn-primary mr-2"> Select </button>
                            </td>
                            <td>
                                <button v-if="draw.id" @click="deleteDrawEntry(rowIndex)" class="btn btn-danger"><i aria-hidden="true" class="fa fa-trash text-center"></i></button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <Tooltip
                    :key="rolloverClickIndex"
                    :position="tooltipPosition"
                    @click-away="hideTooltip"
                    @close="hideTooltip"
                >
                    <div class="container align-middle" style="min-width: 250px;">
                        <div class="row">
                            <div class="col-1 p-0">
                                <input
                                    v-if="!data[rolloverClickIndex]?.is_session_only"
                                    :checked="isIncentiveChecked(rolloverClickIndex, props.activity.id)"
                                    @change="toggleIncentive(rolloverClickIndex, props.activity, $event)"
                                    id="incentive_root"
                                    type="checkbox"
                                    class="form-control"
                                    style="height: 1em; margin-top: 8px;"
                                    :disabled="data[rolloverClickIndex]?.score || data[rolloverClickIndex]?.is_session_only"
                                >
                            </div>
                            <div class="col-10">
                                <label for="incentive_root">{{ props.activity.name }}</label>
                            </div>
                        </div>
                        <div v-for="(incentive, incentiveIndex) in props.activity.incentives"
                             class="row is-side-pot"
                             :key="incentive.id || incentiveIndex"
                        >
                            <div class="col-1 p-0">
                                <input
                                    :id="'incentive_' + incentiveIndex"
                                    type="checkbox" class="form-control"
                                    style="height: 1em; margin-top: 8px;"
                                    :checked="isIncentiveChecked(rolloverClickIndex, incentive.id)"
                                    @change="toggleIncentive(rolloverClickIndex, incentive, $event)"
                                    :disabled="data[rolloverClickIndex]?.score"
                                >
                            </div>
                            <div class="col-10">
                                <label :for="'incentive_' + incentiveIndex">{{ incentive.name }}</label>
                            </div>
                        </div>
                        <template v-for="(carryover, carryoverIndex) in props.carryovers"
                                  class="row"
                                  :key="carryover.id || carryoverIndex"
                        >
                            <div class="row">
                                <div class="col-1 p-0">
                                    <input
                                        :id="'carryover_' + carryoverIndex"
                                        type="checkbox"
                                        class="form-control"
                                        style="height: 1em; margin-top: 8px;"
                                        :checked="isIncentiveChecked(rolloverClickIndex, carryover.id)"
                                        @change="toggleIncentive(rolloverClickIndex, carryover, $event)"
                                        :disabled="data[rolloverClickIndex]?.score"
                                    >
                                </div>
                                <div class="col-10">
                                    <label :for="'carryover_' + carryoverIndex">{{ carryover.name }}(co)</label>
                                </div>
                            </div>

                            <div v-for="(carryoverIncentive, carryoverIncentiveIndex) in carryover.incentives"
                                 class="row is-side-pot"
                                 :key="carryoverIncentive.id || carryoverIncentiveIndex"
                            >
                                    <div class="col-1 p-0">
                                        <input
                                            :id="'carryover_incentive_' + carryoverIncentiveIndex"
                                            type="checkbox" class="form-control"
                                            style="height: 1em; margin-top: 8px;"
                                            :checked="isIncentiveChecked(rolloverClickIndex, carryoverIncentive.id)"
                                            @change="toggleIncentive(rolloverClickIndex, carryoverIncentive, $event)"
                                            :disabled="data[rolloverClickIndex]?.score"
                                        >
                                    </div>
                                    <div class="col-10">
                                        <label :for="'carryover_incentive_' + carryoverIncentiveIndex">{{ carryoverIncentive.name }}</label>
                                    </div>
                                </div>
                        </template>

                    </div>
                </Tooltip>

            </div>
            <!-- Pagination -->
            <div v-if="!loading" class="d-flex flex-column justify-content-center align-items-center">
                <label for="total">Pages</label>
                <div class="d-flex mb-3">
                    <div v-for="(page, pageIndex) in numPages" class="">
                        <button @click="goToPage(pageIndex+1, $event)" class="btn btn-primary" style="margin-right: 10px;"> {{ pageIndex+1 }} </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.save-warning {
    color: #D16CE6;
}
.draw-container {
    display: block;
    padding: 16px 16px 30px 16px;
    border-radius: 4px;
    background-color: #fff;
}
.is-side-pot {
    margin-left: 1rem;
}
.results-area {
    position: absolute;
    display: block;
    top: 100%;
    left: 0;
    min-width: 10rem;
    z-index: 1000;
    background-color: white;
    border: 1px solid #d1d5db;
    list-style: none;
    margin: 0;
    padding: 0;
}
.results-area li {
    padding: .25rem 1.5rem;
    font-weight: 400;
    cursor: pointer;
}
.results-area li.highlighted {
    background-color: #25aae0;
    color: #fff;
}
.results-area li:hover {
    background-color: #25aae0;
    color: #fff;
}
.table {
    background-color: white;
}
.table td, .table th {
    padding: 10px;
    border-top: 1px solid #dee2e6;
}
.table th {
    border-top: none;
    border-bottom: 2px solid #dee2e6;
    vertical-align: middle;
}
table td {
    color: #000;
    font-weight: 400;
    font-size: 13px;
}
.table .form-control:disabled, table .form-control[readonly] {
    background-color: #ccc !important;
    opacity: 1;
}
.btn-outline-dark {
    color: #1e282c;
    border: 1px solid #1e282c;
}
.btn-primary:hover, .btn-primary:focus {
    background: #fff !important;
    border: 1px solid #2199c9 !important;
    color: #2199c9 !important;
}
.btn-outline-dark:hover {
    color: #fff;
    background-color: #1e282c;
    border-color: #1e282c;
}
.btn {
    line-height: 1.5;
    font-size: 1rem;
    font-weight: 400;
    padding: .375rem .75rem;
}
.btn-paging {
    border: none;
    background: none;
    line-height: 1;
    color: rgba(0, 0, 0, .26);
}
.btn-paging:disabled {
    opacity: 50%;
}
.paging {
    margin: 20px 0 10px 0;
    color: rgba(0, 0, 0, .54);
}

.btn-group-sm>.btn, .btn-sm {
    padding: .25rem .5rem;
    font-size: .875rem;
    line-height: 1.5;
    border-radius: .2rem;
}

.bs-popover-left>.arrow, .bs-popover-right>.arrow {
    margin-top: -.5rem;
    top: 50%;
}
.bs-popover-auto[x-placement^=left]>.arrow, .bs-popover-left>.arrow {
    right: calc(-.5rem - 1px);
    width: .5rem;
    height: 1rem;
    margin: .3rem 0;
}
.popover .arrow {
    width: 1rem;
    height: .5rem;
    margin: 0 .3rem;
}
.popover, .popover .arrow {
    position: absolute;
    display: block;
}
.popover .arrow {
    position: absolute;
    display: block;
    width: 1rem;
    height: 0.5rem;
    margin: 0 0.3rem;
}
.fade {
    transition: opacity .15s linear;
}
.fade.show {
    opacity: 1;
}
.bs-popover-auto[x-placement^=left], .bs-popover-left {
    margin-right: .5rem;
}
.popover, .popover .arrow {
    position: absolute;
    display: block;
}
.popover {
    top: 0;
    left: 0;
    z-index: 1060;
    max-width: 276px;
    font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
    text-align: start;
    text-decoration: none;
    text-shadow: none;
    text-transform: none;
    letter-spacing: normal;
    word-break: normal;
    word-spacing: normal;
    white-space: normal;
    line-break: auto;
    font-size: .875rem;
    word-wrap: break-word;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: .3rem;
}
.bs-popover-auto[x-placement^=left]>.arrow:before, .bs-popover-left>.arrow:before {
    right: 0;
    border-width: .5rem 0 .5rem .5rem;
    border-left-color: rgba(0, 0, 0, .25);
}
.bs-popover-auto[x-placement^=left]>.arrow:after, .bs-popover-left>.arrow:after {
    right: 1px;
    border-width: .5rem 0 .5rem .5rem;
    border-left-color: #fff;
}
.popover .arrow:after, .popover .arrow:before {
    position: absolute;
    display: block;
    content: "";
    border-color: transparent;
    border-style: solid;
}
.popover .arrow:after, .popover .arrow:before {
    position: absolute;
    display: block;
    content: "";
    border-color: transparent;
    border-style: solid;
}
.popover-body {
    max-height: 25em;
    overflow-y: auto;
    white-space: pre-wrap;
    padding: .5rem .75rem;
    color: #212529;
}
.tooltip {
    position: absolute;
    left: -999px;
}
.tooltip label {
    display: inline-block;
    margin-bottom: .5rem;
    margin-top: 6px;
    text-transform: uppercase;
    color: #a2aeb9;
    font-weight: 600;
    font-size: 11.5px;
    letter-spacing: 1px;
}
.btn-disabled-custom {
    background-color: #ccc;
    border-color: #ccc;
    color: black;
}

.btn-primary {
    color: white;
}

.btn:disabled {
    opacity: 1 !important;
}

.tooltip-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.tooltip-wrapper .icon {
    width: 20px;
    height: 20px;
}

.tooltip-wrapper .tooltip-text {
    visibility: hidden;
    background-color: #333;
    color: #fff;
    text-align: center;
    border-radius: 4px;
    padding: 4px 8px;
    position: absolute;
    z-index: 100;
    bottom: 125%; /* place above the image */
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;

    /* Fade in effect */
    opacity: 0;
    transition: opacity 0.3s;
}

.tooltip-wrapper:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
}
</style>
